import React, { useCallback, useEffect, useState } from 'react';
import { useAuth, reauthenticateInPlace, getStoredAuthRecord } from './AuthView';
import { getRememberedLoginEmail } from '../utils/rememberedLoginEmail';
import { isConnectorAvailable, tryEnsureFreshToken } from '../lib/connector';
import { isBootTimeTerminalRejection, shouldSuppressAutomaticRefresh } from '../lib/authTerminalLatch';
import {
  getServiceSessionDeadPayload,
  markServiceSessionHealedFromIpc,
  markServiceSessionHealedViaReauth,
  shouldShowServiceSessionBanner,
  subscribeServiceSessionDead,
} from '../lib/serviceSessionDead';
import { STREAMING_RESTORE_BANNER_MESSAGE } from '../lib/authDeclawShadow';
import { diagnosticLog } from '../utils/diagnosticLogger';

const DEFAULT_MESSAGE = STREAMING_RESTORE_BANNER_MESSAGE;

/**
 * Persistent banner when connector service auth is dead but the web session is still valid.
 * Re-auth writes fresh credentials to auth.json without signing the user out of the app.
 */
export default function ServiceSessionDeadBanner() {
  const auth = useAuth();
  const [visible, setVisible] = useState(() => shouldShowServiceSessionBanner(auth.isAuthenticated));
  const [showReauth, setShowReauth] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [bannerLogged, setBannerLogged] = useState(false);

  const syncVisible = useCallback(() => {
    setVisible(shouldShowServiceSessionBanner(auth.isAuthenticated));
  }, [auth.isAuthenticated]);

  useEffect(() => subscribeServiceSessionDead(syncVisible), [syncVisible]);

  useEffect(() => {
    syncVisible();
  }, [auth.isAuthenticated, syncVisible]);

  useEffect(() => {
    if (!visible || bannerLogged) return;
    const payload = getServiceSessionDeadPayload();
    try {
      diagnosticLog('warn', 'other', 'service_session_dead_banner_shown', {
        reason: payload?.reason ?? null,
        trigger: payload?.trigger ?? null,
        remote_access_affected: payload?.remote_access_affected ?? null,
      });
    } catch { /* noop */ }
    setBannerLogged(true);
  }, [visible, bannerLogged]);

  useEffect(() => {
    if (!visible || !isConnectorAvailable()) return;
    if (shouldSuppressAutomaticRefresh() || isBootTimeTerminalRejection()) return;

    const probeHealthy = async () => {
      if (shouldSuppressAutomaticRefresh()) return;
      const outcome = await tryEnsureFreshToken();
      if (outcome.kind === 'ok') {
        markServiceSessionHealedFromIpc();
        setBannerLogged(false);
      }
    };

    void probeHealthy();
    const timer = window.setInterval(() => { void probeHealthy(); }, 120_000);
    return () => window.clearInterval(timer);
  }, [visible]);

  const openReauth = () => {
    const stored = getStoredAuthRecord();
    const remembered = getRememberedLoginEmail();
    setEmail(stored?.user?.email || auth.user?.email || remembered || '');
    setPassword('');
    setError(null);
    setShowReauth(true);
  };

  const handleReauthSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await reauthenticateInPlace(email.trim(), password);
      markServiceSessionHealedViaReauth();
      setShowReauth(false);
      setBannerLogged(false);
      syncVisible();
    } catch (err) {
      setError(err?.message || 'Sign in failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!visible) return null;

  const payload = getServiceSessionDeadPayload();
  const message = payload?.user_message?.trim() || DEFAULT_MESSAGE;

  return (
    <>
      <div
        role="alert"
        data-testid="service-session-dead-banner"
        style={{
          position: 'fixed',
          left: '50%',
          transform: 'translateX(-50%)',
          top: 12,
          zIndex: 10060,
          maxWidth: 'min(640px, calc(100vw - 32px))',
          borderRadius: 12,
          border: '1px solid rgba(245, 158, 11, 0.4)',
          background: 'rgba(20, 20, 32, 0.97)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
          padding: '14px 16px 14px 18px',
          fontSize: 13,
          lineHeight: 1.5,
          color: 'var(--text-secondary, #ccc)',
        }}
      >
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: 'var(--text-primary, #fff)', marginBottom: 4 }}>
              Session expired
            </div>
            <div>{message}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
          <button
            type="button"
            data-testid="service-session-reauth-btn"
            onClick={openReauth}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: 'none',
              background: '#C9A84C',
              color: '#1a1a2e',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Sign In
          </button>
        </div>
      </div>

      {showReauth && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Restore remote access"
          data-testid="service-session-reauth-modal"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10070,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
          onClick={() => !busy && setShowReauth(false)}
        >
          <form
            onSubmit={handleReauthSubmit}
            onClick={(ev) => ev.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 400,
              background: 'var(--bg-primary, #121212)',
              border: '1px solid rgba(201,168,76,0.25)',
              borderRadius: 16,
              padding: 24,
            }}
          >
            <h2 style={{ margin: '0 0 8px', fontSize: 18, color: '#fff' }}>Restore remote access</h2>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#aaa', lineHeight: 1.5 }}>
              Sign in once to refresh background service credentials. Your current session and playback stay active.
            </p>
            {error && (
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#f87171' }}>{error}</p>
            )}
            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={{ display: 'block', fontSize: 12, color: '#888', marginBottom: 4 }}>Email</span>
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #333',
                  background: '#1a1a2e',
                  color: '#fff',
                  fontSize: 14,
                }}
              />
            </label>
            <label style={{ display: 'block', marginBottom: 16 }}>
              <span style={{ display: 'block', fontSize: 12, color: '#888', marginBottom: 4 }}>Password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #333',
                  background: '#1a1a2e',
                  color: '#fff',
                  fontSize: 14,
                }}
              />
            </label>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                disabled={busy}
                onClick={() => setShowReauth(false)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: '1px solid #444',
                  background: 'transparent',
                  color: '#ccc',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#C9A84C',
                  color: '#1a1a2e',
                  fontWeight: 700,
                  cursor: busy ? 'wait' : 'pointer',
                }}
              >
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
